import { rectToDisplay, toDisplay, toUser } from "./geometry.ts";
import { num } from "./serialize.ts";
import type { Moment, Page, Rect } from "./types.ts";

export type ArrangeOperation =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "horizontal-centres"
  | "vertical-centres"
  | "distribute-horizontal"
  | "distribute-vertical";

function bounds(page: Page, moment: Moment): Rect {
  const anchor = moment.anchor;
  if (anchor.shape === "pin") {
    const [x, y] = toDisplay(page, ...anchor.point);
    return [x, y, x, y];
  }
  const box: Rect = [Infinity, Infinity, -Infinity, -Infinity];
  for (const rect of anchor.rects) {
    const r = rectToDisplay(page, rect);
    box[0] = Math.min(box[0], r[0]);
    box[1] = Math.min(box[1], r[1]);
    box[2] = Math.max(box[2], r[2]);
    box[3] = Math.max(box[3], r[3]);
  }
  return box;
}

/** Translate selected anchors in display space, returning only changed moments in input order. */
export function arrangeMoments(page: Page, moments: readonly Moment[], operation: ArrangeOperation): Moment[] {
  const distribute = operation === "distribute-horizontal" || operation === "distribute-vertical";
  if (moments.length < (distribute ? 3 : 2) || moments.some((m) => m.anchor.page !== page.page)) return [];

  const horizontal = operation === "left" || operation === "right" || operation === "horizontal-centres" || operation === "distribute-horizontal";
  const axis = horizontal ? 0 : 1;
  const items = moments.map((moment) => ({ moment, box: bounds(page, moment), delta: 0 }));
  const centre = (item: (typeof items)[number]) => (item.box[axis] + item.box[axis + 2]) / 2;

  if (distribute) {
    const sorted = [...items].sort((a, b) => centre(a) - centre(b) || (a.moment.id < b.moment.id ? -1 : a.moment.id > b.moment.id ? 1 : 0));
    const first = centre(sorted[0]!);
    const step = (centre(sorted[sorted.length - 1]!) - first) / (sorted.length - 1);
    for (let i = 1; i < sorted.length - 1; i++) sorted[i]!.delta = first + step * i - centre(sorted[i]!);
  } else {
    let start = Infinity;
    let end = -Infinity;
    for (const item of items) {
      start = Math.min(start, item.box[axis]);
      end = Math.max(end, item.box[axis + 2]);
    }
    const leading = operation === "left" || operation === "top";
    const trailing = operation === "right" || operation === "bottom";
    const target = leading ? start : trailing ? end : (start + end) / 2;
    for (const item of items) item.delta = target - (leading ? item.box[axis] : trailing ? item.box[axis + 2] : centre(item));
  }

  const origin = toUser(page, 0, 0);
  const changed: Moment[] = [];
  for (const { moment, delta } of items) {
    if (delta === 0) continue;
    const shifted = toUser(page, horizontal ? delta : 0, horizontal ? 0 : delta);
    // Quantize one translation, not each endpoint's half-cent decision: all rects must
    // retain their sizes and spacing even when a target lies between PDF hundredths.
    const dx = num(shifted[0] - origin[0]);
    const dy = num(shifted[1] - origin[1]);
    if (dx === 0 && dy === 0) continue;
    const anchor = moment.anchor;
    if (anchor.shape === "pin") {
      const x = num(anchor.point[0] + dx);
      const y = num(anchor.point[1] + dy);
      if (x !== anchor.point[0] || y !== anchor.point[1]) changed.push({ ...moment, anchor: { ...anchor, point: [x, y] } });
    } else {
      let moved = false;
      const rects = anchor.rects.map((r): Rect => {
        const translated: Rect = [num(r[0] + dx), num(r[1] + dy), num(r[2] + dx), num(r[3] + dy)];
        if (translated.some((v, i) => v !== r[i])) moved = true;
        return translated;
      });
      if (moved) changed.push({ ...moment, anchor: { ...anchor, rects } });
    }
  }
  return changed;
}
